import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight, Plane, Car, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import Plate from "@/components/Plate";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/lib/auth";

function isoDay(d) {
  if (!d) return "";
  if (typeof d === "string") return d.length >= 10 ? d.slice(0, 10) : d;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

// Parse "AAAA-MM-JJ" en date LOCALE — new Date("AAAA-MM-JJ") est interprété
// en UTC minuit, ce qui exclut le dernier jour du mois en fuseau UTC+1/+2.
function localDay(v) {
  const [y, m, d] = isoDay(v).split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function CalendrierPage() {
  const { user } = useAuth();
  const isCommercial = user?.role === "commercial";
  const isAtelier = user?.role === "atelier";
  const [ventes, setVentes] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [v, i] = await Promise.all([api.get("/ventes"), api.get("/interventions")]);
      setVentes(v.data);
      setInterventions(i.data);
      setLoading(false);
    };
    load();
  }, []);

  const ventesMap = useMemo(() => {
    const m = {};
    ventes.forEach((v) => { m[v.id] = v; });
    return m;
  }, [ventes]);

  // Day map: ISO -> { ventes, departs, interventions }
  const { dayMap, saleDays, departDays, interDays, prepDays } = useMemo(() => {
    const map = {};
    const sd = new Set(); const dd = new Set(); const inD = new Set(); const prep = new Set();
    const add = (key, kind, item) => {
      if (!map[key]) map[key] = { ventes: [], departs: [], interventions: [], preparation: [] };
      map[key][kind].push(item);
    };
    for (const v of ventes) {
      if (v.date_vente) { const k = isoDay(v.date_vente); sd.add(k); add(k, "ventes", v); }
      if (v.date_depart) {
        const k = isoDay(v.date_depart);
        dd.add(k);
        add(k, "departs", v);
        if (v.preparer_a_livrer) { prep.add(k); add(k, "preparation", v); }
      }
    }
    for (const it of interventions) {
      if (it.date) {
        const k = isoDay(it.date);
        inD.add(k);
        add(k, "interventions", it);
      }
    }
    return { dayMap: map, saleDays: sd, departDays: dd, interDays: inD, prepDays: prep };
  }, [ventes, interventions]);

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);

  // Aggregate stats for visible month
  const monthVentes = ventes.filter((v) => {
    if (!v.date_vente) return false;
    const d = localDay(v.date_vente);
    return d >= monthStart && d <= monthEnd;
  });
  const monthDeparts = ventes.filter((v) => {
    if (!v.date_depart) return false;
    const d = localDay(v.date_depart);
    return d >= monthStart && d <= monthEnd;
  });
  const monthInters = interventions.filter((it) => {
    if (!it.date) return false;
    const d = localDay(it.date);
    return d >= monthStart && d <= monthEnd;
  });

  const modifiers = {
    saleDay: Array.from(saleDays).map((s) => { const [y,m,d] = s.split("-").map(Number); return new Date(y,m-1,d); }),
    departDay: Array.from(departDays).map((s) => { const [y,m,d] = s.split("-").map(Number); return new Date(y,m-1,d); }),
    interDay: Array.from(interDays).map((s) => { const [y,m,d] = s.split("-").map(Number); return new Date(y,m-1,d); }),
    prepDay: Array.from(prepDays).map((s) => { const [y,m,d] = s.split("-").map(Number); return new Date(y,m-1,d); }),
  };
  const modifiersStyles = {
    saleDay: { fontWeight: 700, color: "#002FA7" },
    departDay: { textDecoration: "underline", textDecorationColor: "#E63946", textDecorationThickness: "2px", textUnderlineOffset: "3px" },
    interDay: { boxShadow: "inset 0 -3px 0 0 #f59e0b" },
    prepDay: { backgroundColor: "#d1fae5" },
  };

  const selectedKey = isoDay(selectedDay);
  const info = dayMap[selectedKey] || { ventes: [], departs: [], interventions: [], preparation: [] };

  if (loading) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
          Planning
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1 flex items-center gap-3">
          <CalendarDays className="text-klein" /> Calendrier
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Visualisez ventes, livraisons, interventions atelier et véhicules à préparer.
        </p>
      </header>

      {/* Stats du mois */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {!isCommercial && !isAtelier && (
          <div className="bg-card border border-border rounded-sm p-5" data-testid="cal-stat-ventes">
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold flex items-center gap-1.5">
              <Car size={12} /> Ventes du mois
            </div>
            <div className="font-display font-black text-3xl tracking-tighter mt-2 text-klein">{monthVentes.length}</div>
          </div>
        )}
        <div className="bg-card border border-border rounded-sm p-5" data-testid="cal-stat-departs">
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold flex items-center gap-1.5">
            <Plane size={12} /> Départs prévus
          </div>
          <div className="font-display font-black text-3xl tracking-tighter mt-2 text-destructive">{monthDeparts.length}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-5" data-testid="cal-stat-inters">
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold flex items-center gap-1.5">
            <Wrench size={12} /> Interventions
          </div>
          <div className="font-display font-black text-3xl tracking-tighter mt-2 text-amber-700">{monthInters.length}</div>
        </div>
        {!isCommercial && (
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold">CA prévisionnel</div>
            <div className="font-display font-black text-3xl tracking-tighter mt-2">
              {money(monthVentes.reduce((s, v) => s + Number(v.prix_vente || 0), 0))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 bg-card border border-border rounded-sm p-5">          <div className="flex items-center justify-between mb-3">
            <Button variant="outline" size="sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-sm" data-testid="cal-prev">
              <ChevronLeft size={14} />
            </Button>
            <div className="font-display font-bold text-lg capitalize">
              {month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
            </div>
            <Button variant="outline" size="sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-sm" data-testid="cal-next">
              <ChevronRight size={14} />
            </Button>
          </div>
          <Calendar
            mode="single"
            selected={selectedDay}
            onSelect={(d) => d && setSelectedDay(d)}
            month={month}
            onMonthChange={setMonth}
            locale={fr}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            className="rounded-sm border-0 p-0 w-full"
            classNames={{
              months: "w-full",
              month: "w-full space-y-3",
              caption: "hidden",
              table: "w-full border-collapse",
              head_row: "flex w-full justify-around",
              row: "flex w-full mt-2 justify-around",
              cell: "h-10 w-10 sm:h-12 sm:w-12 text-center text-sm p-0",
              day: "h-10 w-10 sm:h-12 sm:w-12 p-0 font-normal text-sm hover:bg-slate-100 rounded-sm",
              day_selected: "bg-klein text-white hover:bg-klein hover:text-white",
              day_today: "bg-slate-100 font-bold",
            }}
          />
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="font-bold text-klein">●</span> Vente</span>
            <span className="inline-flex items-center gap-1.5"><span className="border-b-2 border-destructive">__</span> Départ</span>
            <span className="inline-flex items-center gap-1.5"><span className="border-b-2 border-amber-600">__</span> Intervention</span>
            <span className="inline-flex items-center gap-1.5"><span className="px-1.5 bg-emerald-100 rounded-sm">■</span> Prêt à livrer</span>
          </div>
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6 space-y-5" data-testid="cal-day-panel">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold">
              Jour sélectionné
            </div>
            <h3 className="font-display font-bold text-xl mt-0.5 capitalize">
              {selectedDay.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </h3>
          </div>

          {info.ventes.length === 0 && info.departs.length === 0 && info.interventions.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground border border-dashed rounded-sm">
              Aucune activité ce jour.
            </div>
          )}

          {info.preparation.length > 0 && (
            <section>
              <div className="text-[11px] tracking-[0.18em] uppercase text-emerald-800 font-bold mb-2">
                Prêts à livrer · {info.preparation.length}
              </div>
              <ul className="space-y-1">
                {info.preparation.map((v) => (
                  <li key={`p-${v.id}`} className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm flex items-center justify-between gap-3">
                    <Link to={`/ventes/${v.id}`} className="flex items-center gap-2 min-w-0 hover:underline">
                      <Plate value={v.plaque} small />
                      <span className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {info.departs.length > 0 && (
            <section>
              <div className="text-[11px] tracking-[0.18em] uppercase text-destructive font-bold mb-2">
                Départs · {info.departs.length}
              </div>
              <ul className="space-y-1">
                {info.departs.map((v) => (
                  <li key={`d-${v.id}`} className="px-3 py-2 border border-border rounded-sm flex items-center justify-between gap-3">
                    <Link to={`/ventes/${v.id}`} className="flex items-center gap-2 min-w-0 hover:underline">
                      <Plate value={v.plaque} small />
                      <span className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</span>
                    </Link>
                    <StatusBadge statut={v.statut} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {info.ventes.length > 0 && (
            <section>
              <div className="text-[11px] tracking-[0.18em] uppercase text-klein font-bold mb-2">
                Ventes · {info.ventes.length}
              </div>
              <ul className="space-y-1">
                {info.ventes.map((v) => (
                  <li key={`v-${v.id}`} className="px-3 py-2 border border-border rounded-sm flex items-center justify-between gap-3">
                    <Link to={`/ventes/${v.id}`} className="flex items-center gap-2 min-w-0 hover:underline">
                      <Plate value={v.plaque} small />
                      <span className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</span>
                    </Link>
                    <span className="text-xs font-mono">{money(v.prix_vente)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {info.interventions.length > 0 && (
            <section>
              <div className="text-[11px] tracking-[0.18em] uppercase text-amber-700 font-bold mb-2">
                Interventions atelier · {info.interventions.length}
              </div>
              <ul className="space-y-1">
                {info.interventions.map((it) => {
                  const v = ventesMap[it.vente_id];
                  return (
                    <li key={`i-${it.id}`} className="px-3 py-2 border border-border rounded-sm">
                      {v ? (
                        <Link to={`/ventes/${v.id}`} className="flex items-center gap-2 min-w-0 hover:underline">
                          <Plate value={v.plaque} small />
                          <span className="text-sm font-medium truncate">{it.type} · {it.description}</span>
                        </Link>
                      ) : (
                        <span className="text-sm">{it.type} · {it.description}</span>
                      )}
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {it.mecanicien ? `${it.mecanicien} · ` : ""}{money(it.cout_total)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </div>

      {/* Livraisons à venir (30 prochains jours) */}
      <div className="bg-card border border-border rounded-sm" data-testid="cal-livraisons-section">
        <div className="px-6 py-4 border-b">
          <div className="text-[11px] tracking-[0.18em] uppercase text-destructive font-bold flex items-center gap-1.5">
            <Plane size={12} /> Livraisons VO · 30 prochains jours
          </div>
          <h3 className="font-display font-bold text-xl mt-0.5">Planning des livraisons</h3>
        </div>
        <UpcomingDeliveries ventes={ventes} isCommercial={isCommercial} />
      </div>
    </div>
  );
}

function UpcomingDeliveries({ ventes }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const limit = new Date(today);
  limit.setDate(limit.getDate() + 30);

  const list = ventes
    .filter((v) => {
      if (!v.date_depart) return false;
      const d = localDay(v.date_depart);
      return d >= today && d <= limit;
    })
    .sort((a, b) => (a.date_depart || "").localeCompare(b.date_depart || ""));

  if (list.length === 0) {
    return <div className="px-6 py-10 text-center text-sm text-muted-foreground">
      Aucune livraison planifiée dans les 30 prochains jours.
    </div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-6 py-3">Date livraison</th>
            <th className="px-6 py-3">Plaque</th>
            <th className="px-6 py-3">Véhicule</th>
            <th className="px-6 py-3">Client</th>
            <th className="px-6 py-3 text-center">Carte grise</th>
            <th className="px-6 py-3 text-center">À préparer</th>
            <th className="px-6 py-3 text-center">Statut paiement</th>
            <th className="px-6 py-3 text-right">Échéance</th>
          </tr>
        </thead>
        <tbody>
          {list.map((v) => {
            const ddep = new Date(isoDay(v.date_depart));
            const diff = Math.round((ddep - today) / 86400000);
            return (
              <tr key={v.id} className="border-t hover:bg-slate-50">
                <td className="px-6 py-3 whitespace-nowrap font-medium">{dateFR(v.date_depart)}</td>
                <td className="px-6 py-3"><Link to={`/ventes/${v.id}`}><Plate value={v.plaque} small /></Link></td>
                <td className="px-6 py-3"><Link to={`/ventes/${v.id}`} className="hover:underline">{v.marque} {v.modele}</Link></td>
                <td className="px-6 py-3">{v.client_nom}</td>
                <td className="px-6 py-3 text-center">
                  {v.carte_grise_disponible
                    ? <span className="text-emerald-700 text-xs font-semibold">OK</span>
                    : <span className="text-destructive text-xs font-semibold">Manquante</span>}
                </td>
                <td className="px-6 py-3 text-center">
                  {v.preparer_a_livrer
                    ? <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">Prêt</span>
                    : <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className="px-6 py-3 text-center"><StatusBadge statut={v.statut} /></td>
                <td className="px-6 py-3 text-right">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold ${
                    diff === 0 ? "bg-red-100 text-destructive"
                    : diff <= 3 ? "bg-orange-100 text-orange-800"
                    : diff <= 7 ? "bg-amber-100 text-amber-800"
                    : "bg-slate-100 text-slate-700"
                  }`}>
                    {diff === 0 ? "Aujourd'hui" : `J-${diff}`}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
